import React, { useEffect, useMemo } from 'react'
import { useNavigate, useLocation, Outlet } from 'react-router-dom'

import { ENTITLEMENTS } from 'interfaces/entitlements/entitlements'
import { ApplicationEntity } from 'interfaces/applicationEntities/applicationEntity'
import { PERMISSIONS } from 'interfaces/permissions/permissions'

import { RoutesManager } from 'utils/routing/routesManager'

import { useIsEntitled } from 'utils/hooks/useIsEntitled'
import { useQueryParams } from 'utils/hooks/useQueryParams'
import { useIsPermitted } from 'utils/hooks/useIsPermitted'

import { getBannerText } from 'components/ui-layouts/issuesMessage/IssuesMessage'
import { PurposeViewDetails } from 'pages/policyCenter/purposes/view/components/PurposeViewDetails'
import { PurposeViewActions } from 'pages/policyCenter/purposes/view/components/PurposeViewActions'
import { PurposeVersionsMessage } from './components/PurposeVersionsMessage'
import { Banner, Button, Spinner, ViewLayout } from '@ketch-com/deck'
import { NavigationBreadCrumbs } from 'components/appLayout/appNavigation/breadcrumbs/NavigationBreadCrumbs'
import { Box } from '@mui/material'
import { useIsDemoOrg } from 'utils/hooks/useIsDemoOrg'
import { usePurposeViewContext } from 'pages/policyCenter/purposes/view/context/PurposeViewContext'

export const PurposeView: React.FC = () => {
  const { purpose, organizationLanguages, isReady } = usePurposeViewContext()
  const navigate = useNavigate()
  const { search, pathname } = useLocation()
  const { queries } = useQueryParams<{ version?: string }>()
  const { isPermitted } = useIsPermitted()

  const { isEntitled } = useIsEntitled()
  const isEntitledToDemo = isEntitled(ENTITLEMENTS.DEMO)
  const { isDemoOrg } = useIsDemoOrg()

  const breadcrumbs = [
    { title: 'Privacy Program', link: RoutesManager.policyCenter.root.getURL() },
    { title: 'Purposes', link: RoutesManager.policyCenter.purposes.root.getURL() },
    { title: purpose?.name || purpose?.code },
  ]

  const hasExtraLanguages = !!organizationLanguages.length

  const tabs = useMemo(
    () =>
      [
        {
          label: 'Overview',
          link: {
            pathname: RoutesManager.policyCenter.purposes.view.overview.root.getURL({ code: purpose?.code }),
            search,
          },
        },
        {
          label: 'Display',
          link: {
            pathname: RoutesManager.policyCenter.purposes.view.display.root.getURL({ code: purpose?.code }),
            search,
          },
          hidden: !hasExtraLanguages,
        },
        {
          label: 'Legal Bases',
          link: {
            pathname: RoutesManager.policyCenter.purposes.view.legalBases.root.getURL({ code: purpose?.code }),
            search,
          },
        },
        {
          label: 'Trackers',
          link: {
            pathname: RoutesManager.policyCenter.purposes.view.trackers.root.getURL({ code: purpose?.code }),
            search,
          },
        },
        {
          label: 'Data Categories',
          link: {
            pathname: RoutesManager.policyCenter.purposes.view.dataCategories.root.getURL({ code: purpose?.code }),
            search,
          },
          hidden: !isDemoOrg,
        },
        {
          label: 'Documents',
          link: {
            pathname: RoutesManager.policyCenter.purposes.view.documents.root.getURL({ code: purpose?.code }),
            search,
          },
          hidden: !isDemoOrg,
        },
        {
          label: 'Processing Activities',
          link: {
            pathname: RoutesManager.policyCenter.purposes.view.processingActivities.root.getURL({
              code: purpose?.code,
            }),
            search,
          },
          hidden: !isPermitted(PERMISSIONS.PROCESSING_ACTIVITY_READ),
        },
        {
          label: 'Versions',
          link: {
            pathname: RoutesManager.policyCenter.purposes.view.versions.root.getURL({ code: purpose?.code }),
            search,
          },
        },
      ].filter(tab => !tab.hidden),
    [search, hasExtraLanguages, isPermitted, purpose?.code, isDemoOrg],
  )

  // Determine current tab based on pathnam
  const currentTab = useMemo(() => {
    let tabIndex = 0
    tabs.forEach((tab, index) => {
      if (pathname.includes(tab.link.pathname)) {
        tabIndex = index
        return
      }
    })
    return tabIndex
  }, [tabs, pathname])

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    navigate(tabs[newValue].link)
  }

  // Handle edge case where user navigates directly to purpose view page with no tab
  useEffect(() => {
    if (purpose?.code && pathname.endsWith(purpose.code)) {
      navigate(tabs[0].link)
    }
  }, [purpose?.code, pathname, navigate, tabs])

  if (!isReady || !purpose) {
    return (
      <>
        <NavigationBreadCrumbs type="dark" items={breadcrumbs} />
        <Box display="flex" justifyContent="center" alignItems="center" borderRadius={1.375} width="1280px" flex={1}>
          <Spinner size="32px" thickness={2.5} />
        </Box>
      </>
    )
  }

  const { hasIssues, name, code, latest } = purpose
  const isLatest = !queries.version || !!latest

  // NOTE: hardcoded for demo purposes
  // TODO: remove/replace asap
  const entityType =
    isEntitledToDemo && name.toLowerCase().includes('personalization')
      ? ApplicationEntity.DPIA
      : ApplicationEntity.PURPOSE

  const banners = (
    <>
      {!!hasIssues && (
        <Banner
          sx={{
            width: '100%',
          }}
          title={getBannerText(entityType)}
          action={
            isEntitledToDemo &&
            name.toLowerCase().includes('personalization') && (
              <Button
                variant="link"
                onClick={() => {
                  navigate(RoutesManager.policyCenter.purposes.upsert.root.getURL({ code }))
                }}
              >
                Update
              </Button>
            )
          }
          severity="error"
        />
      )}
      {!isLatest && <PurposeVersionsMessage purpose={purpose} />}
    </>
  )
  return (
    <>
      <NavigationBreadCrumbs type="dark" items={breadcrumbs} />
      <Box display="flex" flexDirection="column" gap="24px" alignItems="center" flex="1">
        {banners}
        <ViewLayout
          contentGroupProps={{
            title: name || code,
            showBackButton: true,
            propsBackButton: {
              onClick: () => navigate(RoutesManager.policyCenter.purposes.root.getURL()),
            },
            infoBlockComponent: <PurposeViewDetails purpose={purpose} />,
            actionRightBlockComponent: isLatest && <PurposeViewActions purpose={purpose} />,
          }}
          tabsComponentProps={{
            tabItems: tabs,
            value: currentTab,
            onChange: handleTabChange,
          }}
        >
          <Outlet />
        </ViewLayout>
      </Box>
    </>
  )
}
